export const DEFAULT_TABS_STORAGE_KEY = "m360.default-tabs";

const PERSON_SECTION_DEFAULT_TAB_GROUPS = new Set([
  "people/person",
  "people/membership",
  "people/career",
  "people/education"
]);

const TAB_SLUG = /^[a-z][a-z0-9-]{0,63}$/;
const GROUP_KEY = /^[a-z][a-z0-9/-]{0,127}$/;
const ID_SEGMENT = /^\d+$/;

export function isTabSlug(value: string): boolean {
  return TAB_SLUG.test(value);
}

export function canApplyStoredDefaultTab(group: string): boolean {
  if (!GROUP_KEY.test(group)) {
    return false;
  }

  if (!group.includes("/")) {
    return group === "people";
  }

  return PERSON_SECTION_DEFAULT_TAB_GROUPS.has(group);
}

function isIdSegment(value: string): boolean {
  return ID_SEGMENT.test(value);
}

function pathSegments(pathname: string): string[] {
  return pathname.replace(/\/$/, "").split("/").filter(Boolean);
}

export function sectionGroupKey(pathname: string): string | null {
  const group = groupSegments(pathname).join("/");
  return GROUP_KEY.test(group) ? group : null;
}

export const INDEX_TAB_SLUG = "index";

function groupSegments(pathname: string): string[] {
  return pathSegments(pathname).filter((segment) => !isIdSegment(segment) && !segment.startsWith(":"));
}

export function tabLocation(pathname: string): { group: string; slug: string } | null {
  const segments = groupSegments(pathname);
  if (segments.length === 0) {
    return null;
  }

  if (segments.length === 1) {
    const group = segments[0];
    return GROUP_KEY.test(group) ? { group, slug: INDEX_TAB_SLUG } : null;
  }

  const slug = segments.at(-1);
  if (!slug || !isTabSlug(slug)) {
    return null;
  }

  const group = segments.slice(0, -1).join("/");
  return GROUP_KEY.test(group) ? { group, slug } : null;
}

type DefaultTabs = Record<string, string>;

function readStoredValue(): string | null {
  try {
    return window.localStorage.getItem(DEFAULT_TABS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function parseDefaultTabs(stored: string | null): DefaultTabs {
  if (!stored) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] =>
          GROUP_KEY.test(entry[0]) && typeof entry[1] === "string" && isTabSlug(entry[1])
      )
    );
  } catch {
    return {};
  }
}

function writeDefaultTabs(next: DefaultTabs): void {
  try {
    window.localStorage.setItem(DEFAULT_TABS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort device-local preference.
  }
}

export function getDefaultTabs(): DefaultTabs {
  return parseDefaultTabs(readStoredValue());
}

export function getDefaultTab(group: string): string | null {
  if (!GROUP_KEY.test(group)) {
    return null;
  }

  return getDefaultTabs()[group] ?? null;
}

export function resolveAllowedDefaultTab(group: string, allowedSlugs: readonly string[], fallback: string): string {
  const saved = getDefaultTab(group);
  if (saved && allowedSlugs.includes(saved)) {
    return saved;
  }

  return fallback;
}

export function applyDefaultTabPath(pathname: string): string {
  const path = pathname.replace(/\/$/, "") || "/";
  const group = sectionGroupKey(path);
  if (!group || group.includes("/") || !canApplyStoredDefaultTab(group)) {
    return pathname;
  }

  // Entity URLs share the same area key after numeric IDs are stripped
  // (`/people/27409609` → `people`). Only rewrite the area root itself.
  if (pathSegments(path).length !== group.split("/").length) {
    return pathname;
  }

  const slug = getDefaultTab(group);
  if (!slug || slug === INDEX_TAB_SLUG) {
    return pathname;
  }

  return `${path}/${slug}`;
}

export function hrefWithStoredDefaultTab(href: string, enabled: boolean): string {
  return enabled ? applyDefaultTabHref(href) : href;
}

export function applyDefaultTabHref(href: string): string {
  const hashIndex = href.indexOf("#");
  const withoutHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const search = queryIndex === -1 ? "" : withoutHash.slice(queryIndex);
  const next = applyDefaultTabPath(pathname);
  if (next === pathname) {
    return href;
  }

  return `${next}${search}${hash}`;
}

export function setDefaultTab(group: string, slug: string): void {
  if (!GROUP_KEY.test(group) || !isTabSlug(slug)) {
    return;
  }

  writeDefaultTabs({ ...getDefaultTabs(), [group]: slug });
}

export function clearDefaultTab(group: string): void {
  if (!GROUP_KEY.test(group)) {
    return;
  }

  const next = { ...getDefaultTabs() };
  if (!(group in next)) {
    return;
  }

  delete next[group];

  if (Object.keys(next).length === 0) {
    clearDefaultTabs();
    return;
  }

  writeDefaultTabs(next);
}

export function clearDefaultTabs(): void {
  try {
    window.localStorage.removeItem(DEFAULT_TABS_STORAGE_KEY);
  } catch {
    // Best-effort clear.
  }
}
